import React, { useMemo, useRef } from "react";
import { CalendarDays, Clock3 } from "lucide-react";
import { cn } from "../../lib/utils";
import {
  formatIsoDateTimeToDisplay,
  formatIsoDateToDisplay,
  parseDisplayDateTimeToIso,
  parseDisplayDateToIso,
} from "../../utils/dateFilters";

function nowDateValue(type) {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  if (type === "datetime-local") {
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }
  return `${year}-${month}-${day}`;
}

function normalizeValue(type, value) {
  if (!value) return "";
  if (type === "datetime-local") return parseDisplayDateTimeToIso(value) || "";
  if (type === "date") return parseDisplayDateToIso(value) || "";
  return String(value || "");
}

function openNativePicker(input) {
  if (!input) return;
  if (typeof input.showPicker === "function") {
    input.showPicker();
    return;
  }
  input.focus();
  input.click();
}

export default function DatePickerField({
  label,
  name,
  value = "",
  onChange,
  type = "date",
  placeholder = "Preencha a data",
  required = false,
  disabled = false,
  className = "",
  min = "",
  max = "",
}) {
  const inputRef = useRef(null);
  const isDateTime = type === "datetime-local";
  const isDateType = type === "date";
  const isNativeDateType = isDateType || isDateTime;

  const normalizedValue = useMemo(() => normalizeValue(type, value), [type, value]);
  const normalizedMin = useMemo(() => normalizeValue(type, min), [type, min]);
  const normalizedMax = useMemo(() => normalizeValue(type, max), [type, max]);

  const emitChange = (nextValue) => {
    if (typeof onChange !== "function") return;
    onChange({
      target: {
        name,
        value: nextValue,
      },
    });
  };

  const handleSetNow = () => {
    emitChange(nowDateValue(type));
  };

  const helperText = useMemo(() => {
    if (!isNativeDateType) return "";
    if (!normalizedValue) {
      return isDateTime
        ? "Clique no campo para abrir data e hora do navegador."
        : "Clique no campo para abrir o calendario do navegador.";
    }
    return isDateTime
      ? `Selecionado: ${formatIsoDateTimeToDisplay(normalizedValue)}.`
      : `Selecionado: ${formatIsoDateToDisplay(normalizedValue)}.`;
  }, [isDateTime, isNativeDateType, normalizedValue]);

  return (
    <div className={cn("space-y-1.5", className)}>
      {label && (
        <label className="text-sm font-medium text-foreground">
          {label}
          {required && <span className="ml-1 text-destructive">*</span>}
        </label>
      )}

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <input
            ref={inputRef}
            type={type}
            name={name}
            value={String(normalizedValue || "")}
            min={normalizedMin || undefined}
            max={normalizedMax || undefined}
            disabled={disabled}
            onChange={(event) => emitChange(event.target.value)}
            placeholder={placeholder}
            className={cn(
              "h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm transition-colors",
              isNativeDateType ? "pr-10" : "",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            )}
          />

          {isNativeDateType && (
            <button
              type="button"
              disabled={disabled}
              aria-label="Abrir calendario"
              onClick={() => openNativePicker(inputRef.current)}
              className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-lg text-muted-foreground transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            >
              <CalendarDays className="h-4 w-4" />
            </button>
          )}
        </div>

        {isNativeDateType && (
          <button
            type="button"
            disabled={disabled}
            onClick={handleSetNow}
            className={cn(
              "inline-flex h-10 shrink-0 items-center gap-1 rounded-lg border border-input bg-background px-3 text-xs font-medium text-muted-foreground transition",
              "hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            )}
          >
            <Clock3 className="h-3.5 w-3.5" />
            {isDateTime ? "Agora" : "Hoje"}
          </button>
        )}
      </div>

      {helperText && <p className="text-xs text-muted-foreground">{helperText}</p>}
    </div>
  );
}
